import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { CompanyService } from '../../../services/company.service';
import { StateService } from '../../../services/state.service';
import { CityService } from '../../../services/city.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { CommonModule } from '@angular/common';
import { State } from '../../../models/state.model';
import { City } from '../../../models/city.model';
import { MessageService } from 'primeng/api';
import { ModuleAccessService, CompanyModuleItem } from '../../../services/module-access.service';

@Component({
  selector: 'app-form-company',
  imports: [
    CommonModule,
    ButtonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TabsModule,
    CheckboxModule,
    SelectModule,
    ToastModule,
    InputNumberModule
  ],
  providers: [MessageService],
  templateUrl: './form-company.component.html'
})
export class FormCompanyComponent implements OnInit {

  form!: FormGroup;
  isEditMode = false;
  id!: number;
  states: State[] = [];
  cities: City[] = []; 
  companyModules: CompanyModuleItem[] = [];
  isLoadingModules = false;
  isSavingModules = false;

  interestTypeOptions = [
    { label: 'Juros simples', value: false },
    { label: 'Juros compostos', value: true },
  ];

  constructor(
    private fb: FormBuilder,
    private companyService: CompanyService,
    private stateService: StateService,
    private cityService: CityService,
    private moduleAccessService: ModuleAccessService,
    private router: Router,
    private location: Location,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      cnpj: [''],
      nickname: [''],
      ie: [''],
      phone: [''],
      email: [''],
      zipCode: [''],
      address: [''],
      addressNumber: [''],
      complement: [''],
      district: [''],
      city: [''],
      state: [''],
      financialChargeInterest: [false],
      financialInterestMonthlyPercent: [0, [Validators.min(0), Validators.max(100)]],
      financialInterestCompound: [false],
      financialInterestGraceDays: [0, [Validators.min(0), Validators.max(3650)]],
    });

    this.syncFinancialInterestFieldsEnabled();

    this.form.get('financialChargeInterest')?.valueChanges.subscribe(() => {
      this.syncFinancialInterestFieldsEnabled();
    });

    this.getStates();

    // Sempre busca a empresa com id 1
    this.id = 1;
    this.isEditMode = true;
    
    this.companyService.find(this.id).subscribe({
      next: (data) => {
        const companyData = data.data as any;
        const savedCity = companyData?.city;
        
        // Se já tiver estado selecionado, carrega as cidades primeiro
        if (companyData?.state) {
          this.loadCitiesAndSetValue(companyData.state, savedCity, companyData);
        } else {
          // Se não tiver estado, apenas faz o patchValue
          this.form.patchValue(companyData);
          this.syncFinancialInterestFieldsEnabled();
        }
      },
      error: (error) => {
        console.log(error);
      }
    })

    this.loadCompanyModules();
  }


  getStates() {
    this.stateService.getAll('', false, null).subscribe({
      next: (result) => {
        this.states = result.data || [];
      }
    });
  }

  getCities(event: any) {
    // Limpa a cidade selecionada quando muda o estado
    this.form.patchValue({ city: '' });
    this.cities = [];

    if (event && event.value) {
      this.cityService.getAll('', null, event.value, false, null).subscribe({
        next: (data) => {
          this.cities = data.data || [];
        }
      });
    }
  }

  loadCitiesAndSetValue(stateAcronym: string, savedCity: string, companyData: any) {
    this.cityService.getAll('', null, stateAcronym, false, null).subscribe({
      next: (data) => {
        this.cities = data.data || [];
        // Agora faz o patchValue com todos os dados, incluindo a cidade
        this.form.patchValue(companyData);
        this.syncFinancialInterestFieldsEnabled();
      }
    });
  }

  goBack() {
    this.location.back();
  }

  onSubmit() {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    if (!raw.financialChargeInterest) {
      raw.financialInterestMonthlyPercent = 0;
      raw.financialInterestCompound = false;
      raw.financialInterestGraceDays = 0;
    }

    if(this.isEditMode){
      this.companyService.update(this.id, raw).subscribe({
          next: (data)=> {
            this.messageService.add({ 
              severity: 'success', 
              summary: 'Sucesso!', 
              detail: 'Empresa atualizada com sucesso!', 
              life: 3000 
            });
          },
          error: (error) => {
            this.showErrorMessage(error);
          }
        });
    }else{
      this.companyService.create(this.form.value).subscribe({
          next: (data)=> {
            this.messageService.add({ 
              severity: 'success', 
              summary: 'Sucesso!', 
              detail: 'Empresa cadastrada com sucesso!', 
              life: 3000 
            });
          },
          error: (error) => {
            this.showErrorMessage(error);
          }
        });
    }
  }


  showErrorMessage(error: any) {
    let msg = 'Erro ao salvar empresa!';
    if(error.error){
      if(error.error.message){
        msg = error.error.message;
      }
      if(error.error.details && error.error.details.length > 0){
        error.error.details.forEach((d: any) => {
          this.messageService.add({ 
            severity: 'error', 
            summary: 'Erro!', 
            detail: d, 
            life: 4000 
          });
        });
      } else {
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro!', 
          detail: msg, 
          life: 4000 
        });
      }
    } else {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Erro!', 
        detail: msg, 
        life: 4000 
      });
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  private syncFinancialInterestFieldsEnabled(): void {
    const on = !!this.form.get('financialChargeInterest')?.value;
    const names = ['financialInterestMonthlyPercent', 'financialInterestCompound', 'financialInterestGraceDays'];
    for (const name of names) {
      const c = this.form.get(name);
      if (!c) continue;
      if (on) {
        c.enable({ emitEvent: false });
      } else {
        c.disable({ emitEvent: false });
      }
    }
  }

  loadCompanyModules(): void {
    this.isLoadingModules = true;
    this.moduleAccessService.getCompanyModules(this.id).subscribe({
      next: (response) => {
        this.companyModules = response.data ?? [];
        this.isLoadingModules = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Erro ao carregar módulos da empresa',
          life: 4000
        });
        this.isLoadingModules = false;
      }
    });
  }

  toggleModuleCard(module: CompanyModuleItem): void {
    module.enabled = !module.enabled;
  }

  saveCompanyModules(): void {
    const payload = this.companyModules.map((module) => ({
      moduleId: module.moduleId,
      enabled: module.enabled
    }));

    this.isSavingModules = true;
    this.moduleAccessService.updateCompanyModules(this.id, payload).subscribe({
      next: ({ data }) => {
        this.companyModules = data ?? [];
        this.moduleAccessService.setEnabledModules(
          this.companyModules.filter((x) => x.active && x.enabled).map((x) => x.code)
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Módulos da empresa atualizados com sucesso!',
          life: 3000
        });
        this.isSavingModules = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Erro ao salvar módulos da empresa',
          life: 4000
        });
        this.isSavingModules = false;
      }
    });
  }

}

